import * as React from "react";
import { Loader2, ShieldCheck, Sparkles } from "lucide-react";
import {
  Button,
  Checkbox,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  toast,
} from "@eduviaos/ui-kit";
import {
  PERMISSION_GROUPS,
  resolveRoleCeiling,
  type PermissionId,
  type StaffDirectoryEntry,
  type StaffDirectoryRole,
  type UserRole,
} from "@eduviaos/shared";
import { useDesignations, useSuggestDesignationScopes } from "@/hooks/useDesignations";
import { useEnabledModules } from "@/hooks/useEnabledModules";
import { useUpdateStaffAccess } from "@/hooks/useStaff";
import { clientFacingText, roleLabelFor } from "@/lib/verticalProfile";

const ROLE_OPTIONS: ReadonlyArray<{ value: StaffDirectoryRole; label: string }> = [
  { value: "admin", label: "Admin ceiling" },
  { value: "teacher", label: "Teacher ceiling" },
  { value: "staff", label: "Staff ceiling" },
];

const STARTERS: ReadonlyArray<{
  label: string;
  role: StaffDirectoryRole;
  permissions: PermissionId[];
}> = [
  {
    label: "Floor operations",
    role: "admin",
    permissions: [
      "view_students",
      "manage_students",
      "view_attendance",
      "manage_attendance",
      "manage_zones",
      "manage_bookings",
      "view_staff",
    ],
  },
  {
    label: "Accounts",
    role: "admin",
    permissions: [
      "view_fees",
      "manage_fees",
      "view_accounting",
      "manage_accounting",
      "view_payroll",
      "manage_payroll",
      "view_team_pay",
      "manage_team_pay",
      "manage_team_attendance",
      "approve_team_attendance",
    ],
  },
];

export function StaffAccessEditorSheet({
  open,
  onOpenChange,
  staff,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  staff: StaffDirectoryEntry | null;
}) {
  const designations = useDesignations();
  const update = useUpdateStaffAccess();
  const suggest = useSuggestDesignationScopes();
  const { vertical } = useEnabledModules();
  const say = (text: string) => clientFacingText(vertical ?? "school", text);
  const [suggestNote, setSuggestNote] = React.useState<string | null>(null);
  const [designationId, setDesignationId] = React.useState("__custom__");
  const [inheritPosition, setInheritPosition] = React.useState(false);
  const [role, setRole] = React.useState<StaffDirectoryRole>("staff");
  const [granted, setGranted] = React.useState<Set<PermissionId>>(new Set());

  React.useEffect(() => {
    if (!open || !staff) return;
    setDesignationId(staff.designation_id ?? "__custom__");
    setInheritPosition(staff.access_inherits_designation);
    setRole(staff.role);
    setGranted(new Set(staff.permission_overrides as PermissionId[]));
  }, [open, staff]);

  const ceiling = React.useMemo(() => resolveRoleCeiling(role as UserRole), [role]);
  const rows = React.useMemo(
    () =>
      PERMISSION_GROUPS.map((group) => ({
        ...group,
        permissions: group.permissions.filter((permission) => ceiling.has(permission.id)),
      })).filter((group) => group.permissions.length > 0),
    [ceiling],
  );

  const selectDesignation = (id: string) => {
    setDesignationId(id);
    setInheritPosition(id !== "__custom__");
    if (id === "__custom__") return;
    const selected = designations.data?.items.find((item) => item.id === id);
    if (!selected) return;
    setRole(selected.role as StaffDirectoryRole);
    setGranted(new Set(selected.permission_overrides as PermissionId[]));
  };

  const applyStarter = (index: number) => {
    const starter = STARTERS[index];
    if (!starter) return;
    setDesignationId("__custom__");
    setInheritPosition(false);
    setRole(starter.role);
    setGranted(new Set(starter.permissions));
  };

  const suggestAccess = async () => {
    if (!staff) return;
    setSuggestNote("Looking at this person's work…");
    try {
      const result = await suggest.mutateAsync({
        label: staff.designation || staff.full_name || "Team member",
        role: role as UserRole,
        vertical: vertical ?? undefined,
      });
      const next = new Set(granted);
      result.suggested_overrides.forEach((permission) => {
        const cast = permission as PermissionId;
        if (ceiling.has(cast)) next.add(cast);
      });
      setGranted(next);
      setInheritPosition(false);
      if (result.parse_error) {
        setSuggestNote("No suggestion came back. Choose the access below yourself.");
        return;
      }
      if (result.suggested_overrides.length === 0) {
        setSuggestNote("Nothing extra was suggested. The boxes below are unchanged.");
        return;
      }
      setSuggestNote(
        `Suggested ${result.suggested_overrides.length} permissions. Review the boxes, then save.`,
      );
    } catch {
      setSuggestNote("Couldn't suggest access. You can still choose it below.");
    }
  };

  const save = async () => {
    if (!staff) return;
    try {
      await update.mutateAsync({
        userId: staff.id,
        body: {
          designation_id: designationId === "__custom__" ? null : designationId,
          role,
          permission_mode: "replace",
          permission_overrides: inheritPosition ? undefined : Array.from(granted).sort(),
        },
      });
      toast.success(`Updated access for ${staff.full_name || staff.email}.`);
      onOpenChange(false);
    } catch {
      toast.error("Couldn't update access. Please retry.");
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="flex w-full flex-col gap-0 p-0 sm:max-w-2xl">
        <SheetHeader className="border-border/60 border-b p-6">
          <SheetTitle>Access for {staff?.full_name || staff?.email || "team member"}</SheetTitle>
          <SheetDescription>
            Assign a reusable position, then tailor this person&apos;s exact access. Unchecked means
            no access. You cannot grant anything outside your own authority.
          </SheetDescription>
        </SheetHeader>

        <div className="min-h-0 flex-1 space-y-6 overflow-y-auto p-6">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>Position</Label>
              <Select value={designationId} onValueChange={selectDesignation}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__custom__">Custom for this person</SelectItem>
                  {(designations.data?.items ?? []).map((designation) => (
                    <SelectItem key={designation.id} value={designation.id}>
                      {designation.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Security ceiling</Label>
              <Select
                value={role}
                onValueChange={(value) => {
                  const nextRole = value as StaffDirectoryRole;
                  const nextCeiling = resolveRoleCeiling(nextRole as UserRole);
                  setRole(nextRole);
                  setInheritPosition(false);
                  setGranted(
                    new Set(
                      Array.from(granted).filter((permission) => nextCeiling.has(permission)),
                    ),
                  );
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ROLE_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {roleLabelFor(vertical ?? "school", option.value, option.label)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {designationId !== "__custom__" ? (
            <Label className="border-border/60 flex items-start gap-3 rounded-lg border p-4">
              <Checkbox
                checked={inheritPosition}
                onCheckedChange={(checked) => setInheritPosition(checked === true)}
                className="mt-0.5"
              />
              <span>
                <span className="block text-sm font-medium">Keep access synced with position</span>
                <span className="text-muted-foreground block text-xs">
                  Future changes to this position update this person automatically. Turn this off to
                  keep a person-specific override.
                </span>
              </span>
            </Label>
          ) : null}

          <div className="flex flex-wrap gap-2">
            {STARTERS.map((starter, index) => (
              <Button
                key={starter.label}
                type="button"
                variant="outline"
                size="sm"
                onClick={() => applyStarter(index)}
              >
                {starter.label}
              </Button>
            ))}
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={suggest.isPending}
              onClick={() => void suggestAccess()}
            >
              {suggest.isPending ? (
                <Loader2 aria-hidden="true" className="size-4 animate-spin" />
              ) : (
                <Sparkles aria-hidden="true" className="size-4" />
              )}
              Suggest access
            </Button>
          </div>
          {suggestNote ? (
            <p className="text-muted-foreground text-sm" role="status">
              {suggestNote}
            </p>
          ) : null}

          <div className="space-y-5">
            {rows.map((group) => (
              <fieldset key={group.key} className="border-border/60 rounded-lg border p-4">
                <legend className="px-1 text-sm font-medium">{say(group.label)}</legend>
                <p className="text-muted-foreground mb-3 text-xs">{say(group.description)}</p>
                <div className="grid gap-2 sm:grid-cols-2">
                  {group.permissions.map((permission) => (
                    <Label
                      key={permission.id}
                      htmlFor={`staff-access-${permission.id}`}
                      className="hover:bg-muted/40 flex cursor-pointer items-start gap-3 rounded-md p-2"
                    >
                      <Checkbox
                        id={`staff-access-${permission.id}`}
                        checked={granted.has(permission.id)}
                        onCheckedChange={(checked) => {
                          const next = new Set(granted);
                          if (checked === true) next.add(permission.id);
                          else next.delete(permission.id);
                          setGranted(next);
                          setInheritPosition(false);
                        }}
                        className="mt-0.5"
                      />
                      <span>
                        <span className="block text-sm font-medium">{say(permission.label)}</span>
                        <span className="text-muted-foreground block text-xs">
                          {say(permission.helper)}
                        </span>
                      </span>
                    </Label>
                  ))}
                </div>
              </fieldset>
            ))}
          </div>
        </div>

        <div className="border-border/60 flex items-center justify-between border-t p-6">
          <span className="text-muted-foreground inline-flex items-center gap-1.5 text-xs">
            <ShieldCheck aria-hidden="true" className="size-4" />
            {granted.size} exact permission{granted.size === 1 ? "" : "s"}
          </span>
          <div className="flex gap-2">
            <SheetClose asChild>
              <Button variant="ghost" disabled={update.isPending}>
                Cancel
              </Button>
            </SheetClose>
            <Button disabled={update.isPending} onClick={() => void save()}>
              {update.isPending ? (
                <Loader2 aria-hidden="true" className="size-4 animate-spin" />
              ) : null}
              Save access
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
